import React from 'react';
import { Calendar, Clock, Users, Video, ExternalLink, FileTextIcon, CheckCircleIcon, MoreVerticalIcon, EditIcon, Trash2Icon } from 'lucide-react';
import { Coffee } from './coffeeData';
interface CoffeeCardProps {
  coffee: Coffee;
  onClick: () => void;
  onEdit?: (coffee: Coffee) => void;
  onDelete?: (coffee: Coffee) => void;
}
const CoffeeCard: React.FC<CoffeeCardProps> = ({
  coffee,
  onClick,
  onEdit,
  onDelete
}) => {
  const isAdmin = coffee.type === 'admin';
  const now = new Date();
  const coffeeDate = new Date(coffee.date);
  const isPast = coffeeDate < now;
  const isJoined = coffee.participants.some(p => p.id === 'current-user');
  const hasParticipated = isPast && isJoined;
  const isHost = coffee.host.id === 'current-user';
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Platform logos
  const getPlatformLogo = (platform: string) => {
    switch (platform) {
      case 'Google Meet':
        return 'https://uploadthingy.s3.us-west-1.amazonaws.com/hawwkzWf3TnSE8Q31Hrx5c/images.png';
      case 'Microsoft Teams':
        return 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg/2203px-Microsoft_Office_Teams_%282018%E2%80%93present%29.svg.png';
      case 'Zoom':
        return 'https://uploadthingy.s3.us-west-1.amazonaws.com/a6M2ckhEkmcVgkXUVkifgg/zoom_logo_icon_147196.png';
      default:
        return null;
    }
  };
  const handleCardClick = (e: React.MouseEvent) => {
    // Only trigger the main card click if we're not clicking on the menu or its buttons
    if (!(e.target as HTMLElement).closest('.coffee-card-menu')) {
      onClick();
    }
  };
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(coffee);
    }
  };
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      if (window.confirm('Sei sicuro di voler eliminare questo evento?')) {
        onDelete(coffee);
      }
    }
  };
  return <div className={`bg-white rounded-xl border ${isAdmin ? 'border-custom-green' : 'border-gray-200'} 
      overflow-hidden h-full flex flex-col hover:shadow-md transition-shadow cursor-pointer relative`} onClick={handleCardClick} data-id="element-272">
      {isAdmin && <div className="bg-custom-green text-white text-xs font-medium py-1 px-4 text-center" data-id="element-273">
          Evento Greenback
        </div>}

      <div className="p-5 flex-1 flex flex-col" data-id="element-274">
        <div className="flex items-start mb-4" data-id="element-275">
          <div className={`w-12 h-12 rounded-full overflow-hidden mr-3 flex-shrink-0 flex items-center justify-center ${isAdmin ? 'bg-green-600' : 'bg-white'}`} data-id="element-276">
            {isAdmin ? <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback" className="w-7 h-7 object-contain brightness-0 invert" data-id="element-277" /> : <img src={coffee.host.avatar} alt={coffee.host.name} className="w-full h-full object-cover" data-id="element-278" />}
          </div>
          <div className="flex-1" data-id="element-279">
            <h3 className="text-base font-semibold text-gray-800 mb-1" data-id="element-280">
              {coffee.title}
            </h3>
            <p className="text-sm text-gray-500 mb-2" data-id="element-281">
              Ospitato da {coffee.host.name}
            </p>
          </div>
          {isHost && !isPast && <div className="coffee-card-menu ml-2" data-id="element-282">
              <div className="flex space-x-1" data-id="element-283">
                <button onClick={handleEditClick} className="p-1 rounded-full hover:bg-gray-100" title="Modifica evento" data-id="element-284">
                  <EditIcon size={14} className="text-gray-500" data-id="element-285" />
                </button>
                <button onClick={handleDeleteClick} className="p-1 rounded-full hover:bg-gray-100" title="Elimina evento" data-id="element-286">
                  <Trash2Icon size={14} className="text-gray-500" data-id="element-287" />
                </button>
              </div>
            </div>}
        </div>

        <div className="flex flex-wrap gap-2 mb-4" data-id="element-288">
          {coffee.topics.map((topic, index) => <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs" data-id="element-289">
              {topic}
            </span>)}
          {hasParticipated && <span className="px-2 py-1 bg-custom-green/10 text-custom-green rounded-full text-xs flex items-center" data-id="element-290">
              <CheckCircleIcon size={12} className="mr-1" data-id="element-291" />
              Hai partecipato
            </span>}
          {isHost && !isPast && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center" data-id="element-292">
              Sei l'host
            </span>}
        </div>

        <div className="flex items-center text-sm text-gray-500 mb-1" data-id="element-293">
          <Calendar size={16} className="mr-2" data-id="element-294" />
          {formatDate(coffee.date)}
        </div>
        <div className="flex items-center text-sm text-gray-500 mb-1" data-id="element-295">
          <Clock size={16} className="mr-2" data-id="element-296" />
          {formatTime(coffee.date)} - {formatTime(coffee.endTime)}
        </div>
        <div className="flex items-center text-sm text-gray-500 mb-4" data-id="element-297">
          {getPlatformLogo(coffee.platform) ? <img src={getPlatformLogo(coffee.platform)} alt={coffee.platform} className="w-5 h-5 mr-2 object-contain" data-id="element-298" /> : <Video size={16} className="mr-2" data-id="element-299" />}
          {coffee.platform}
        </div>

        <p className="text-sm text-gray-600 mb-4" data-id="element-300">{coffee.description}</p>

        {isAdmin && <div className="flex items-center mb-4" data-id="element-301">
            <Users size={16} className="text-gray-500 mr-2" data-id="element-302" />
            <span className="text-sm text-gray-500" data-id="element-303">
              {coffee.participants.length} partecipanti
            </span>
          </div>}

        <div className="mt-auto" data-id="element-304">
          {isPast ? hasParticipated ? <button className="w-full flex items-center justify-center py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-305">
                <FileTextIcon size={16} className="mr-2" data-id="element-306" />
                Dettagli
              </button> : <button disabled className="w-full py-2 bg-gray-200 text-gray-500 rounded-lg font-medium" data-id="element-307">
                Terminato
              </button> : <button className="w-full flex items-center justify-center py-2 bg-custom-green text-white rounded-lg hover:bg-custom-green/90 transition-colors" data-id="element-308">
              <ExternalLink size={16} className="mr-2" data-id="element-309" />
              Partecipa
            </button>}
        </div>
      </div>
    </div>;
};
export default CoffeeCard;